/**
 * /admin/metrics — Internal SaaS metrics dashboard.
 *
 * Surfaces:
 *  - Tier distribution + monthly vs annual split
 *  - Revenue by tier × cycle (12mo)
 *  - MRR / ARR / paying users
 *  - Weekly purchases + weekly signups (last 12 weeks)
 *  - 30-day downgrade count
 *
 * Owner / Admin only.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, Navigate } from 'react-router-dom';
import { ArrowLeft, BarChart3, TrendingUp, Users, Repeat, Crown, MailPlus, Target } from 'lucide-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line,
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { isAdminOrAbove } from '../lib/tier';
import PageTitle from '../components/PageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_COLOURS = { free: '#F59E0B', pro: '#0F172A', pro_plus: '#7C3AED' };
const CYCLE_COLOURS = { monthly: '#0052FF', annual: '#7C3AED' };

const fmtMoney = (v) =>
  `£${Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtCompact = (v) => {
  const n = Number(v || 0);
  if (n >= 1_000_000) return `£${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `£${(n / 1_000).toFixed(1)}k`;
  return `£${n.toFixed(0)}`;
};

const KpiCard = ({ label, value, icon: Icon, hint, testid }) => (
  <div className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs uppercase tracking-wider font-bold text-[#64748B]">{label}</span>
      {Icon ? <Icon className="w-4 h-4 text-[#0052FF]" /> : null}
    </div>
    <div className="text-2xl font-extrabold text-[#020617]">{value}</div>
    {hint ? <div className="text-xs text-[#94A3B8] mt-1">{hint}</div> : null}
  </div>
);

const AdminMetrics = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  // Default ON — keeps the dashboard accurate by hiding any rows tagged
  // is_test=true or source in {e2e-test, test, qa}. Toggle off if you
  // need the full raw numbers (debugging / QA day).
  const [excludeTest, setExcludeTest] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/admin/metrics/overview`, {
          withCredentials: true,
          params: { exclude_test_data: excludeTest },
        });
        setData(r.data);
        setErr(null);
      } catch (e) {
        console.error('metrics load failed', e);
        setErr('Could not load metrics.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [excludeTest]);

  if (!user) return <Navigate to="/login" replace />;
  if (!isAdminOrAbove(user)) return <Navigate to="/home" replace />;
  // Only show the full-page loading state on the FIRST load — subsequent
  // toggles of the test-data filter keep the page visible.
  if (loading && !data) return <div className="p-8 text-center text-[#64748B]">Loading metrics…</div>;
  if (err || !data) return <div className="p-8 text-center text-[#DC2626]">{err || 'No data.'}</div>;

  const { totals, tier_distribution, tier_cycle_breakdown, cycle_split,
    revenue_12mo, weekly_revenue, weekly_signups, waitlist } = data;

  const cycleTotal = (cycle_split.monthly || 0) + (cycle_split.annual || 0);
  const monthlyPct = cycleTotal > 0 ? ((cycle_split.monthly / cycleTotal) * 100).toFixed(0) : 0;
  const annualPct = cycleTotal > 0 ? ((cycle_split.annual / cycleTotal) * 100).toFixed(0) : 0;

  return (
    <div className="max-w-6xl mx-auto p-6 sm:p-8" data-testid="admin-metrics-page">
      <Link to="/admin/tiers" className="inline-flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0052FF] mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to admin
      </Link>

      <div className="flex items-start justify-between gap-4 mb-6">
        <PageTitle
          title="Forge Ledger metrics"
          subtitle="Live business metrics — MRR, tier distribution, conversion."
          className="mb-0"
        />

        {/* Test-data toggle: filter out is_test=true / source in {e2e-test,test,qa}
            rows so MRR + cohort numbers stay accurate during QA. Default ON. */}
        <label
          className="inline-flex items-center gap-2 cursor-pointer select-none mt-1"
          data-testid="metrics-exclude-test-toggle"
        >
          <input
            type="checkbox"
            checked={excludeTest}
            onChange={(e) => setExcludeTest(e.target.checked)}
            className="w-4 h-4 accent-[#0052FF] cursor-pointer"
          />
          <span className="text-xs font-bold uppercase tracking-wider text-[#64748B]">
            Exclude test data
          </span>
        </label>
      </div>

      {/* KPI ROW */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <KpiCard
          label="MRR"
          value={fmtCompact(totals.mrr)}
          icon={TrendingUp}
          hint={`ARR ≈ ${fmtCompact(totals.arr)}`}
          testid="kpi-mrr"
        />
        <KpiCard
          label="Paying users"
          value={totals.paying_users}
          icon={Users}
          hint={`${totals.free_users} on Ember`}
          testid="kpi-paying"
        />
        <KpiCard
          label="Conversion · 30d"
          value={`${totals.conversion_rate_30d ?? 0}%`}
          icon={Target}
          hint={
            (totals.signups_30d ?? 0) > 0
              ? `${totals.converted_30d}/${totals.signups_30d} paid · lifetime ${totals.conversion_rate_lifetime ?? 0}%`
              : `No signups in 30d · lifetime ${totals.conversion_rate_lifetime ?? 0}%`
          }
          testid="kpi-conversion"
        />
        <KpiCard
          label="Comped"
          value={totals.comped_users}
          icon={Crown}
          hint="Free-of-charge accounts"
          testid="kpi-comped"
        />
        <KpiCard
          label="Downgrades · 30d"
          value={totals.downgrades_30d}
          icon={Repeat}
          testid="kpi-downgrades"
        />
        <Link to="/admin/waitlist" className="block group" data-testid="kpi-waitlist-link">
          <KpiCard
            label="Waitlist"
            value={waitlist?.total ?? 0}
            icon={MailPlus}
            hint={
              (waitlist?.landing_page || 0) + (waitlist?.titanium || 0) > 0
                ? `${waitlist.landing_page} launch · ${waitlist.titanium} Titanium · view all →`
                : 'Pre-launch email captures · view all →'
            }
            testid="kpi-waitlist"
          />
        </Link>
      </div>

      {/* Tier distribution + cycle split */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="chart-tier-distribution">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#475569] mb-4">Tier distribution (paying)</h2>
          {tier_distribution.filter(t => t.tier !== 'free').length === 0 ? (
            <p className="text-sm text-[#94A3B8] text-center py-12">No paying users yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={tier_distribution.filter(t => t.tier !== 'free')}
                  dataKey="count"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  label={(entry) => `${entry.label}: ${entry.count}`}
                >
                  {tier_distribution.filter(t => t.tier !== 'free').map((t) => (
                    <Cell key={t.tier} fill={TIER_COLOURS[t.tier] || '#64748B'} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="chart-cycle-split">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#475569] mb-4">Monthly vs Annual</h2>
          {cycleTotal === 0 ? (
            <p className="text-sm text-[#94A3B8] text-center py-12">No purchases yet.</p>
          ) : (
            <>
              <div className="flex items-end gap-4 mb-4 mt-6">
                <div className="flex-1">
                  <div className="text-3xl font-extrabold" style={{ color: CYCLE_COLOURS.monthly }}>{monthlyPct}%</div>
                  <div className="text-xs uppercase tracking-wider text-[#64748B]">Monthly · {cycle_split.monthly}</div>
                </div>
                <div className="flex-1 text-right">
                  <div className="text-3xl font-extrabold" style={{ color: CYCLE_COLOURS.annual }}>{annualPct}%</div>
                  <div className="text-xs uppercase tracking-wider text-[#64748B]">Annual · {cycle_split.annual}</div>
                </div>
              </div>
              <div className="flex h-3 rounded-full overflow-hidden">
                <div style={{ width: `${monthlyPct}%`, backgroundColor: CYCLE_COLOURS.monthly }} />
                <div style={{ width: `${annualPct}%`, backgroundColor: CYCLE_COLOURS.annual }} />
              </div>
              <p className="text-xs text-[#94A3B8] mt-3">
                Annual plans give a ~17% discount on Damascus, ~17% on Steel. Higher annual share = better cash flow + lower churn.
              </p>
            </>
          )}
        </div>
      </div>

      {/* Tier × cycle breakdown table */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 mb-6" data-testid="chart-tier-cycle-breakdown">
        <h2 className="text-sm font-bold uppercase tracking-wider text-[#475569] mb-4">
          Active subscriptions by tier × cycle
        </h2>
        {tier_cycle_breakdown.length === 0 ? (
          <p className="text-sm text-[#94A3B8] text-center py-6">No paying users yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E2E8F0] text-[#64748B] text-xs uppercase tracking-wider">
                <th className="text-left py-2 font-semibold">Tier</th>
                <th className="text-right py-2 font-semibold">Monthly</th>
                <th className="text-right py-2 font-semibold">Annual</th>
                <th className="text-right py-2 font-semibold">Total</th>
              </tr>
            </thead>
            <tbody>
              {tier_cycle_breakdown.map((row) => (
                <tr key={row.tier} className="border-b border-[#F1F5F9] last:border-0">
                  <td className="py-2 font-semibold text-[#020617]" style={{ color: TIER_COLOURS[row.tier] }}>{row.label}</td>
                  <td className="py-2 text-right text-[#475569]">{row.monthly}</td>
                  <td className="py-2 text-right text-[#475569]">{row.annual}</td>
                  <td className="py-2 text-right font-bold text-[#020617]">{row.monthly + row.annual}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Revenue by tier × cycle (12mo) */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 mb-6" data-testid="chart-revenue-by-key">
        <h2 className="text-sm font-bold uppercase tracking-wider text-[#475569] mb-4">Revenue by tier × cycle (last 12 months)</h2>
        {revenue_12mo.length === 0 ? (
          <p className="text-sm text-[#94A3B8] text-center py-12">No revenue yet.</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={revenue_12mo}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="label" stroke="#64748B" tick={{ fontSize: 11 }} />
              <YAxis stroke="#64748B" tick={{ fontSize: 11 }} tickFormatter={fmtCompact} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Bar dataKey="revenue" fill="#0052FF" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Weekly purchases + signups */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="chart-weekly-revenue">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#475569] mb-4">Weekly revenue (12wk)</h2>
          {weekly_revenue.length === 0 ? (
            <p className="text-sm text-[#94A3B8] text-center py-12">No purchases yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={weekly_revenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="week" stroke="#64748B" tick={{ fontSize: 10 }} />
                <YAxis stroke="#64748B" tick={{ fontSize: 10 }} tickFormatter={fmtCompact} />
                <Tooltip formatter={(v) => fmtMoney(v)} />
                <Line type="monotone" dataKey="revenue" stroke="#0052FF" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="chart-weekly-signups">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#475569] mb-4">Weekly signups (12wk)</h2>
          {weekly_signups.length === 0 ? (
            <p className="text-sm text-[#94A3B8] text-center py-12">No signups yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={weekly_signups}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="week" stroke="#64748B" tick={{ fontSize: 10 }} />
                <YAxis stroke="#64748B" tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="signups" fill="#7C3AED" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <p className="text-xs text-[#94A3B8] leading-relaxed">
        <strong className="text-[#475569]">Data source:</strong> aggregated from <code>payment_transactions</code> (Stripe webhook writes)
        and <code>users</code> (signup/tier state). MRR is computed by taking each user&apos;s most recent paid transaction
        and converting annual prices to monthly-equivalent (÷12). When we migrate to true Stripe Subscriptions
        (see <code>PRE_DEPLOY_CHECKLIST.md</code>), MRR will come from Stripe&apos;s native subscription state.
      </p>
    </div>
  );
};

export default AdminMetrics;
